import { defineComponent as _defineComponent } from 'vue'
import ReportConstructorWrap from "@/components/statistics/extended-report/ReportConstructorWrap.vue";
import ColumnsCheckboxes from "@/components/statistics/ColumnsCheckboxes.vue";
import { computed, onBeforeMount, ref, watch } from "vue";
import TrashBtn from "@/components/base/buttons/TrashBtn.vue";
import FilterSubIds from "@/components/statistics/extended-report/FilterSubIds.vue";
import FiltersDesign from "@/components/base/filters/FiltersDesign.vue";
import { useRoute, useRouter } from "vue-router/composables";
import store from "@/store";
import PageTabs from "@/components/base/PageTabs.vue";
import { useWindowResize } from "@/utils/useWindowSize";
import { IColumnFilter } from "@/types";
import { EventTraceFilters, EventTracerColumns } from "@/services/TablePage/eventTracer";
import { getOneEmployeesOffer } from "@/api/offers";
import { showServerError } from "@/utils";
import { IOfferAdditionalUrl } from "@/api/types/offers";
import ReportFilterByDate from "@/components/statistics/extended-report/ReportFilterByDate.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'EventTracerConstructor',
  emits: ['submit', 'clear'],
  setup(__props, { expose, emit }) {

const route = useRoute();
const router = useRouter();
const selected = ref<string[]>([]);
const filters = ref<EventTraceFilters>({});
const columns = ref<IColumnFilter[]>([]);
const filtersComp = ref<FiltersDesign>(null);



const currentTab = ref(0);
const tabs = [
    { tab: 'Колонки' },
    { tab: 'Фильтры' },
];
const changeTab = (tab: number) => currentTab.value = tab;

const { width } = useWindowResize();
const isMobile = computed(() => width.value < 577);

const getDateFilter = computed(() => {
    return filters.value.filters.find((filter) => filter.id === 'event_date');
});

const emptyConditions = {
    isEmptyDate: !store.getters['GET_FILTERS_PARAMS_OBJECT'].event_date_from ||
        !store.getters['GET_FILTERS_PARAMS_OBJECT'].event_date_to,
};
const getEmptyConditions = computed(() => {
    return getDateFilter.value?.select.length > 0 ? {} : emptyConditions;
});

onBeforeMount(() => {
    filters.value = new EventTraceFilters();
    columns.value = new EventTracerColumns().columns;
    setDefaultFilters();
    updateSelectedColumns();
});

const setDefaultFilters = () => {
    const filtersWithDefaults = filters.value.filters.filter((filter) => filter.defaultValue);
    const paramsObj = {};
    filtersWithDefaults.forEach((filter) => {
        filter.select = filter.defaultValue;
        if (['is_bot', 'is_valid'].includes(filter.id)) {
            const val = filter.defaultValue === 'true';
            const params = { [filter.id]: val };
            filter.select = val;
            Object.assign(paramsObj, params);
        } else if (filter.id === 'event_date') {
            const params = {
                event_date_from: filter.defaultValue[0],
                event_date_to: filter.defaultValue[1],
            };
            Object.assign(paramsObj, params);
        } else {
            const params = {
                [filter.id]: filter.defaultValue,
            };
            Object.assign(paramsObj, params);
        }
    });
    store.commit('SET_FILTERS_PARAMS_OBJECT', paramsObj);
};

const setDefaultColumns = () => {
    selected.value = columns.value.flatMap(i => i.items.filter(j => j.default).map(k => k.value));
    selected.value.unshift('event_id');
    updateUrl();
};

const updateSelectedColumns = () => {
    const cols = route.query.columns;
    if (!cols || (Array.isArray(cols) && cols.length === 0)) {
        setDefaultColumns();
        return;
    }
    selected.value = cols;
};

watch(() => route.query, () => {
    updateUrl();
});

const updateUrl = () => {
    const data = { columns: [...selected.value] };
    const query = Object.keys(route.query)?.length > 0 ? { ...route.query, ...data } : data;
    router.replace({ name: router.currentRoute.name || '', query }).catch((error) => {
        if (error.name !== 'NavigationDuplicated') {
            throw error;
        }
    });
};

const submitFilters = () => {
    filtersComp.value.submit();
};

const currentOffer = computed(() => {
    if (!filters.value.filters) return [];
    const offerFilter = filters.value.filters.find((filter) => filter.id === 'offer_id');
    return offerFilter ? offerFilter.select : [];
});

const getGoalFilter = computed(() => {
    return filters.value.filters.find((filter) => filter.id === 'offer_goal_id');
});
const getOfferURLFilter = computed(() => {
    return filters.value.filters.find((filter) => filter.id === 'offer_url_id');
});

watch(() => currentOffer.value, () => {
    if (!filters.value.filters) return;
    getGoalFilter.value!.disabled = currentOffer.value.length <= 0;
    getOfferURLFilter.value!.disabled = currentOffer.value.length <= 0;
    if (currentOffer.value.length <= 0) {
        getGoalFilter.value!.clear();
        getOfferURLFilter.value!.clear();
    }
    if (getGoalFilter.value && currentOffer.value.length > 1) {
        getGoalFilter.value!.tooltip = 'Фильтр по цели возможен только если отчёт отфильтрован по одному офферу';
        getOfferURLFilter.value!.tooltip = 'Фильтр по цели возможен только если отчёт отфильтрован по одному офферу';
    }
    if (getGoalFilter.value && currentOffer.value.length === 1) {
        setOfferData(currentOffer.value[0].legacy_id);
    }
}, { deep: true });

const setOfferData = async (id: number) => {
    try {
        const { goals, additional_urls } = await getOneEmployeesOffer(id, { by_legacy_id: 1 });
        setGoals(goals);
        setUrls(additional_urls);
    } catch (e) {
        showServerError(e, 'Список целей оффера не загружен');
    }
};

const setGoals = (goals) => {
    getGoalFilter.value!.items = goals;

    if (route.query.hasOwnProperty('offer_goal_id') && getGoalFilter.value!.select.length <= 0) {
        const goalIds = route.query.offer_goal_id;
        if (!goalIds || goalIds === 'NaN') return;
        getGoalFilter.value!.select = Array.isArray(goalIds) ? goalIds.map(g => +g!) : [Number(goalIds)];
    }
};

const setUrls = (additional_urls: IOfferAdditionalUrl[]) => {
    const defaultUrl = { legacy_id: 0, name: 'По умолчанию' };
    getOfferURLFilter.value!.items = [defaultUrl, ...additional_urls];

    if (route.query.hasOwnProperty('offer_url_id') && getOfferURLFilter.value!.select.length <= 0) {
        const urlIds = route.query.offer_url_id;
        if (!urlIds || urlIds === 'NaN') return;
        getOfferURLFilter.value!.select = Array.isArray(urlIds) ? urlIds.map(i => +i!) : [Number(urlIds)];
    }
};

const clearFilters = () => {
    filters.value.clearFilters();
    store.commit('SET_FILTERS_PARAMS_OBJECT', {});
    emit('clear');
};

const clearAll = () => {
    filters.value.clearFilters();
    store.commit('SET_FILTERS_PARAMS_OBJECT', {});
    setDefaultColumns();
    setDefaultFilters();
    emit('clear');
};

expose({ selected });

return { __sfc: true,route, router, selected, filters, columns, filtersComp, emit, currentTab, tabs, changeTab, width, isMobile, getDateFilter, emptyConditions, getEmptyConditions, setDefaultFilters, setDefaultColumns, updateSelectedColumns, updateUrl, submitFilters, currentOffer, getGoalFilter, getOfferURLFilter, setOfferData, setGoals, setUrls, clearFilters, clearAll, ReportConstructorWrap, ColumnsCheckboxes, TrashBtn, FilterSubIds, FiltersDesign, PageTabs, ReportFilterByDate }
}

})